import { useCallback, useEffect, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  onSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { WeekCard } from "@/components/WeekCard";
import type { WeekDoc } from "@/lib/types";
import { getAppConfig } from "@/lib/shiftConfig";

type WeekWithStats = WeekDoc & { registered: number };

export function Dashboard() {
  const [weeks, setWeeks] = useState<WeekWithStats[]>([]);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let unsub: (() => void) | null = null;
    (async () => {
      const cfg = await getAppConfig();
      const empSnap = await getDocs(collection(db, "employees"));
      setTotalEmployees(empSnap.size);

      const q = query(collection(db, "weeks"), orderBy("startDate", "desc"));
      unsub = onSnapshot(q, async (snap) => {
        const ws: WeekWithStats[] = await Promise.all(
          snap.docs.map(async (d) => {
            const data = d.data();
            const regs = await getDocs(collection(db, "weeks", d.id, "registrations"));
            return {
              label: data.label as string,
              startDate: data.startDate as string,
              endDate: data.endDate as string,
              status: data.status as WeekDoc["status"],
              createdAt: 0,
              registered: regs.size,
            };
          }),
        );
        setWeeks(ws);
        setLoading(false);
        void cfg;
      });
    })();
    return () => unsub?.();
  }, []);

  const handleCreateWeek = useCallback(async () => {
    setCreating(true);
    setMsg(null);
    try {
      const openWeekFn = httpsCallable(functions, "openWeek");
      const result = await openWeekFn();
      const data = result.data as { weekId: string; label: string; created: boolean };
      setMsg(data.created
        ? `✅ Đã tạo tuần ${data.label}`
        : `⚠️ Tuần ${data.label} đã tồn tại`);
    } catch (err: unknown) {
      setMsg(`❌ ${err instanceof Error ? err.message : "Lỗi tạo tuần"}`);
    } finally {
      setCreating(false);
    }
  }, []);

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Đang tải dashboard…</div>;
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-black text-gray-900">Tuần</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{weeks.length} tuần</span>
          <button
            onClick={handleCreateWeek}
            disabled={creating}
            className="rounded-2xl bg-pink-500 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-pink-600 disabled:opacity-50"
          >
            {creating ? "Đang tạo…" : "+ Tạo tuần mới"}
          </button>
        </div>
      </div>

      {msg && (
        <div className="mb-4 rounded-xl bg-pink-50 p-3 text-center text-sm font-medium text-pink-600">
          {msg}
        </div>
      )}

      {weeks.length === 0 ? (
        <div className="rounded-3xl bg-white p-8 text-center shadow-lg ring-1 ring-pink-100">
          <div className="text-4xl">📅</div>
          <p className="mt-3 text-gray-500">Chưa có tuần nào. Nhấn nút trên để tạo tuần mới.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {weeks.map((w) => (
            <WeekCard key={w.startDate} week={w} registered={w.registered} total={totalEmployees} />
          ))}
        </div>
      )}
    </div>
  );
}
